import { useIsAuthenticated } from '@azure/msal-react';
import { Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Header } from 'components/Header';
import { Sidebar } from 'components/Sidebar';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

export type ProtectedProps = {
  children: React.ReactNode;
};

const sidebarWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  height: '100%',
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${sidebarWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

/**
 * Layout for from authenticated users.
 */
export function Logged({ children }: ProtectedProps) {
  const isAuthenticated = useIsAuthenticated();
  const router = useRouter();
  const [ready, setReady] = useState(false);
  const [open, setOpen] = React.useState(true);

  const toggleSidebar = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (ready) {
      return;
    }

    if (!isAuthenticated && router.pathname !== '/login') {
      router.push('/login');
    } else {
      setReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, router]);

  if (!ready || !isAuthenticated) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={24} thickness={7} />
      </Box>
    );
  }

  return (
    <>
      <Header handleSidebar={toggleSidebar} />
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 64px)',
        }}
      >
        <Sidebar open={open} width={sidebarWidth} />
        <Main open={open}>{children}</Main>
      </Box>
    </>
  );
}
